import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import panelModes, { PanelMode } from './panelModes';
import dockedPanelWidth, { DOCKED_PANEL_WIDTH } from './dockedPanelWidth';
import useWidthBesideScrollbar from '../../hooks/useWidthBesideScrollbar';

export const PANEL_MODE_STORAGE_KEY = 'pingwin.panelMode';

// Storage can be blocked or full, and the panel then opens floating as it did before.
const readStoredMode = (): PanelMode => {
  try {
    const stored = localStorage.getItem(PANEL_MODE_STORAGE_KEY);
    return stored === panelModes.DOCKED ? panelModes.DOCKED : panelModes.FLOATING;
  } catch {
    return panelModes.FLOATING;
  }
};

const storeMode = (mode: PanelMode) => {
  try {
    localStorage.setItem(PANEL_MODE_STORAGE_KEY, mode);
  } catch {
    // nothing to do
  }
};

type PinguDock = {
  open: boolean;
  setOpen: (open: boolean) => void;
  // Kept while there is no room to dock, so the panel docks again once there is.
  mode: PanelMode;
  setMode: (mode: PanelMode) => void;
  canDock: boolean;
  docked: boolean;
  // The docked panel's width, narrowed to the room beside the page.
  dockedWidth: number;
};

const PinguDockContext = createContext<PinguDock>({
  open: false,
  setOpen: () => {},
  mode: panelModes.FLOATING,
  setMode: () => {},
  canDock: false,
  docked: false,
  dockedWidth: DOCKED_PANEL_WIDTH,
});

// Wraps the page rather than living in the launcher, so the page narrows in the same
// render as the panel docks and floats, and the choice survives closing the panel.
// The choice is also kept in localStorage, so it survives a reload on this browser.
export const PinguDockContextProvider = ({
  sidebarWidth,
  children,
}: {
  sidebarWidth: number;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // A remembered dock only applies while there is room for it, through canDock below.
  const [mode, setStateMode] = useState<PanelMode>(readStoredMode);
  const setMode = useCallback((nextMode: PanelMode) => {
    setStateMode(nextMode);
    storeMode(nextMode);
  }, []);
  const widthBesideScrollbar = useWidthBesideScrollbar();

  const roomToDock = dockedPanelWidth(widthBesideScrollbar, sidebarWidth);
  const canDock = roomToDock !== null;
  const dockedWidth = roomToDock ?? DOCKED_PANEL_WIDTH;
  const docked = open && mode === panelModes.DOCKED && canDock;

  const value = useMemo(() => ({
    open,
    setOpen,
    mode,
    setMode,
    canDock,
    docked,
    dockedWidth,
  }), [open, mode, setMode, canDock, docked, dockedWidth]);

  return (
    <PinguDockContext.Provider value={value}>
      {children}
    </PinguDockContext.Provider>
  );
};

export const useDockContext = () => useContext(PinguDockContext);
